import { useEffect, useMemo, useRef, useState } from "react";
import { geoGraticule10, geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { Plane } from "lucide-react";
import { useApi } from "@/lib/api";

const W = 800;
const H = 400;
const MAX_SCALE = 1500;
const LEG_MS = 2800;
const PAUSE_MS = 600;
const JET = "M0,-13 C1.6,-13 2.2,-10 2.2,-6 L2.2,-2 L12,4 L12,6.5 L2.2,3.2 L2.2,8 L5.2,10.6 L5.2,12.4 L0,11 L-5.2,12.4 L-5.2,10.6 L-2.2,8 L-2.2,3.2 L-12,6.5 L-12,4 L-2.2,-2 L-2.2,-6 C-2.2,-10 -1.6,-13 0,-13 Z";

let landPromise = null;
const loadLand = () => {
  landPromise = landPromise || import("world-atlas/land-50m.json").then((m) => feature(m.default || m, (m.default || m).objects.land));
  return landPromise;
};

function useLocations(values, preset) {
  const api = useApi();
  const [points, setPoints] = useState({});
  const key = values.join("|");
  useEffect(() => {
    if (preset) return undefined;
    if (!values.length) { setPoints({}); return undefined; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        values.forEach((v) => params.append("v", v));
        const { data } = await api.get(`/airports/locate?${params.toString()}`);
        setPoints(data);
      } catch { setPoints({}); }
    }, 300);
    return () => clearTimeout(t);
  }, [key, preset]); // eslint-disable-line react-hooks/exhaustive-deps
  return preset || points;
}

function curve(a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const bend = Math.min(0.28, 60 / Math.max(Math.hypot(dx, dy), 1) + 0.12);
  const c = [(a[0] + b[0]) / 2 - dy * bend, (a[1] + b[1]) / 2 + dx * bend];
  const at = (t) => [(1 - t) ** 2 * a[0] + 2 * (1 - t) * t * c[0] + t * t * b[0], (1 - t) ** 2 * a[1] + 2 * (1 - t) * t * c[1] + t * t * b[1]];
  const angle = (t) => {
    const tx = 2 * (1 - t) * (c[0] - a[0]) + 2 * t * (b[0] - c[0]);
    const ty = 2 * (1 - t) * (c[1] - a[1]) + 2 * t * (b[1] - c[1]);
    return (Math.atan2(ty, tx) * 180) / Math.PI + 90;
  };
  return { d: `M${a[0]},${a[1]} Q${c[0]},${c[1]} ${b[0]},${b[1]}`, at, angle };
}

function useProjection(segments) {
  return useMemo(() => {
    const coords = segments.flatMap((s) => [[s.from.lon, s.from.lat], [s.to.lon, s.to.lat]]);
    const p = geoNaturalEarth1();
    if (!coords.length) return p.fitExtent([[10, 10], [W - 10, H - 10]], { type: "Sphere" });
    p.fitExtent([[90, 70], [W - 90, H - 70]], { type: "MultiPoint", coordinates: coords });
    if (p.scale() > MAX_SCALE || !Number.isFinite(p.scale())) {
      const lon = coords.reduce((s, c) => s + c[0], 0) / coords.length;
      const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
      p.scale(MAX_SCALE).rotate([-lon, 0]).center([0, lat]).translate([W / 2, H / 2]);
    }
    return p;
  }, [segments]);
}

const REDUCED = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Per-frame progress goes straight to the DOM; React state only changes per leg so navigation transitions aren't starved.
function useLoopProgress(count, onFrame) {
  const [leg, setLeg] = useState(0);
  const frame = useRef(onFrame);
  frame.current = onFrame;
  useEffect(() => {
    if (!count || REDUCED()) { setLeg(0); return undefined; }
    let raf;
    let last = -1;
    const start = performance.now();
    const tick = (now) => {
      const cycle = (now - start) % (count * (LEG_MS + PAUSE_MS));
      const l = Math.floor(cycle / (LEG_MS + PAUSE_MS));
      const local = cycle - l * (LEG_MS + PAUSE_MS);
      const t = Math.min(local / LEG_MS, 1);
      if (l !== last) { last = l; setLeg(l); }
      frame.current(l, t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [count]);
  return leg;
}

const planeTransform = (arc, t) => { const [x, y] = arc.at(t); return `translate(${x},${y}) rotate(${arc.angle(t)}) scale(1.15)`; };

function Jet({ innerRef, transform, testId }) {
  return (
    <g ref={innerRef} transform={transform} data-testid={testId}>
      <path d={JET} fill="#0B1530" opacity="0.35" transform="translate(2,3)" />
      <path d={JET} fill="#FFFFFF" stroke="#C6A55C" strokeWidth="0.8" />
      <circle cx="-12" cy="5.3" r="1.5" fill="#FF3B3B" className="nav-light" data-testid="route-map-light-port" />
      <circle cx="12" cy="5.3" r="1.5" fill="#3BFF7A" className="nav-light" data-testid="route-map-light-starboard" />
    </g>
  );
}

export default function RouteMap({ legs, points: preset, className = "mb-8", testId = "route-map", live = null }) {
  const values = useMemo(() => [...new Set(legs.flatMap((l) => [l.from_airport, l.to_airport]).map((v) => (v || "").trim()).filter(Boolean))], [legs]);
  const points = useLocations(values, preset);
  const [land, setLand] = useState(null);
  useEffect(() => { loadLand().then(setLand).catch(() => {}); }, []);

  const segKey = legs.map((l) => `${points[(l.from_airport || "").trim()]?.code}-${points[(l.to_airport || "").trim()]?.code}`).join(",");
  const segments = useMemo(() => legs.map((l, i) => ({ i, from: points[(l.from_airport || "").trim()], to: points[(l.to_airport || "").trim()] }))
    .filter((s) => s.from && s.to && s.from.code !== s.to.code), [segKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const projection = useProjection(segments);
  const path = useMemo(() => geoPath(projection), [projection]);
  const arcs = useMemo(() => segments.map((s) => curve(projection([s.from.lon, s.from.lat]), projection([s.to.lon, s.to.lat]))), [segments, projection]);
  const stops = useMemo(() => {
    const m = new Map();
    segments.forEach((s) => [s.from, s.to].forEach((p) => m.set(p.code, { ...p, xy: projection([p.lon, p.lat]) })));
    return [...m.values()];
  }, [segments, projection]);
  const progressRef = useRef(null);
  const planeRef = useRef(null);
  const liveArc = live ? segments.findIndex((s) => s.i === live.leg) : -1;
  const loopLeg = useLoopProgress(live ? 0 : arcs.length, (l, t) => {
    const a = arcs[l];
    if (!a) return;
    progressRef.current?.setAttribute("stroke-dashoffset", String(1 - t));
    planeRef.current?.setAttribute("transform", planeTransform(a, t));
  });
  const leg = live ? liveArc : loopLeg;
  const t0 = live ? live.t : REDUCED() ? 0.5 : 0;
  const cur = arcs[leg];
  const svgRef = useRef(null);

  return (
    <section className={`on-navy relative overflow-hidden border border-[#C6A55C]/25 bg-[#0B1530] max-lg:rounded-2xl ${className}`} data-testid={testId}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto" role="img" aria-label="Trip route map">
        <defs>
          <radialGradient id="rm-glow" cx="50%" cy="45%" r="70%">
            <stop offset="0%" stopColor="#14224A" />
            <stop offset="100%" stopColor="#0A1228" />
          </radialGradient>
        </defs>
        <rect width={W} height={H} fill="url(#rm-glow)" />
        <path d={path(geoGraticule10())} fill="none" stroke="#C6A55C" strokeOpacity="0.07" strokeWidth="0.6" />
        {land && <path d={path(land)} fill="#2E4A8F" fillOpacity="0.6" stroke="#C6A55C" strokeOpacity="0.4" strokeWidth="0.6" />}
        {arcs.map((a, i) => (
          <g key={i} data-testid={`route-map-leg-${i}`}>
            <path d={a.d} fill="none" stroke="#C6A55C" strokeOpacity={i === leg ? 0.35 : 0.55} strokeWidth="1.4" strokeDasharray="3 5" />
            {i === leg && <path ref={progressRef} d={a.d} fill="none" stroke="#E8C979" strokeWidth="2" pathLength="1" strokeDasharray="1 1" strokeDashoffset={1 - t0} strokeLinecap="round" />}
          </g>
        ))}
        {stops.map((s) => (
          <g key={s.code} transform={`translate(${s.xy[0]},${s.xy[1]})`} data-testid={`route-map-stop-${s.code}`}>
            <circle r="9" fill="#C6A55C" fillOpacity="0.15" className="route-pulse" />
            <circle r="3.6" fill="#E8C979" stroke="#0B1530" strokeWidth="1.2" />
            <text y="-12" textAnchor="middle" fill="#FFFFFF" fontSize="13" letterSpacing="1.5" style={{ fontFamily: "'Times New Roman', Times, serif" }}>{s.code}</text>
            {s.city && <text y="20" textAnchor="middle" fill="#C9D3EA" fontSize="9.5" letterSpacing="1.2" style={{ textTransform: "uppercase" }}>{s.city}</text>}
          </g>
        ))}
        {cur && <Jet innerRef={planeRef} transform={planeTransform(cur, t0)} testId="route-map-plane" />}
      </svg>
      <div className="absolute left-4 top-3 flex items-center gap-2 text-[0.6rem] tracking-[0.24em] uppercase text-[#E8C979]">
        <Plane size={12} /> {live ? "Live progress" : "Route preview"}{arcs.length > 1 && leg >= 0 ? ` · Leg ${leg + 1} of ${arcs.length}` : ""}
      </div>
      {!arcs.length && (
        <p className="absolute inset-x-0 bottom-4 text-center text-xs text-[#C9D3EA] px-6" data-testid={`${testId}-empty`}>
          Choose your departure and arrival airports to see the route.
        </p>
      )}
    </section>
  );
}
